import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  HelpCircle, Home, ArrowLeft, Search, ChevronDown,
  User, Lock, Mail, Package, MessageCircle, Shield, AlertTriangle,
  CheckCircle, XCircle, Wrench, FileText, Phone, MapPin, Clock,
  CreditCard, Flag, Trash2, Camera, BookOpen, Info, ThumbsUp, ThumbsDown, Eye
} from 'lucide-react';

export default function HelpPage() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [faqVotes, setFaqVotes] = useState({});

  // 📊 Données des catégories
  const categories = [
    { id: 'all', label: 'Tout', icon: BookOpen, color: 'blue', gradient: 'from-blue-500 to-cyan-500' },
    { id: 'account', label: 'Compte', icon: User, color: 'purple', gradient: 'from-purple-500 to-pink-500' },
    { id: 'listing', label: 'Annonces', icon: Package, color: 'orange', gradient: 'from-orange-500 to-yellow-500' },
    { id: 'contact', label: 'Contact', icon: MessageCircle, color: 'green', gradient: 'from-green-500 to-emerald-500' },
    { id: 'security', label: 'Sécurité', icon: Shield, color: 'red', gradient: 'from-red-500 to-pink-500' },
    { id: 'technical', label: 'Technique', icon: Wrench, color: 'yellow', gradient: 'from-yellow-500 to-orange-500' }
  ];

  // 📚 Données des FAQs
  const faqData = [
    // COMPTE & INSCRIPTION
    {
      id: 1,
      category: 'account',
      question: 'Comment créer un compte ?',
      answer: `1. Cliquez sur "S'inscrire" en haut de la page
2. Renseignez vos informations (nom ou pseudo, email, téléphone)
3. Validez votre compte via l'email reçu
4. Connectez-vous et commencez à utiliser MonMarket

💡 Conseil : Utilisez une adresse email valide pour recevoir les notifications importantes.`,
      icon: User,
      tags: ['inscription', 'compte', 'créer', 'nouveau'],
      views: 1245,
      helpful: 89,
      notHelpful: 5
    },
    {
      id: 2,
      category: 'account',
      question: 'J\'ai oublié mon mot de passe',
      answer: `Utilisez la fonction "Mot de passe oublié" sur la page de connexion. Un lien de réinitialisation vous sera envoyé par email.

⚠️ Si vous ne recevez pas l'email :
• Vérifiez vos spams
• Assurez-vous d'avoir entré la bonne adresse email
• Attendez quelques minutes et réessayez`,
      icon: Lock,
      tags: ['mot de passe', 'oublié', 'réinitialiser', 'connexion'],
      views: 892,
      helpful: 78,
      notHelpful: 3
    },
    {
      id: 3,
      category: 'account',
      question: 'Comment modifier mes informations personnelles ?',
      answer: `1. Connectez-vous à votre compte
2. Accédez à votre profil (icône utilisateur)
3. Cliquez sur "Modifier le profil"
4. Modifiez les informations souhaitées
5. Enregistrez les modifications

Vous pouvez modifier : nom, email, téléphone, adresse, photo de profil.`,
      icon: User,
      tags: ['profil', 'modifier', 'informations', 'éditer'],
      views: 567,
      helpful: 45,
      notHelpful: 2
    },
    {
      id: 4,
      category: 'account',
      question: 'Comment supprimer mon compte ?',
      answer: `Pour supprimer votre compte définitivement :

📧 Envoyez une demande à contact@monmarket.ci depuis l'adresse email associée à votre compte.

⚠️ Attention : Cette action est irréversible. Toutes vos données seront supprimées :
• Vos annonces publiées
• Votre historique de commandes
• Vos informations personnelles

La suppression sera effective sous 48h.`,
      icon: Trash2,
      tags: ['supprimer', 'compte', 'fermer', 'désactiver'],
      views: 234,
      helpful: 12,
      notHelpful: 1
    },

    // ANNONCES & PUBLICATIONS
    {
      id: 5,
      category: 'listing',
      question: 'Comment publier une annonce ?',
      answer: `1. Connectez-vous à votre compte
2. Cliquez sur "Publier une annonce"
3. Ajoutez des photos claires et de qualité (jusqu'à 8 photos)
4. Renseignez une description précise et détaillée
5. Indiquez un prix réaliste
6. Sélectionnez la catégorie appropriée
7. Ajoutez votre localisation
8. Publiez

💡 Conseils pour vendre rapidement :
• Prenez des photos sous différents angles
• Soyez honnête sur l'état du produit
• Fixez un prix compétitif
• Répondez rapidement aux messages`,
      icon: Camera,
      tags: ['publier', 'annonce', 'vendre', 'créer', 'nouveau'],
      views: 2103,
      helpful: 156,
      notHelpful: 8
    },
    {
      id: 6,
      category: 'listing',
      question: 'Quels types de produits sont autorisés ?',
      answer: `✅ Vous pouvez publier :
• Produits électroniques et informatiques
• Vêtements et accessoires
• Meubles et équipements de maison
• Véhicules et pièces détachées
• Livres, jeux et loisirs
• Services légaux (cours, réparations, etc.)
• Autres biens licites

🚫 Sont strictement interdits :
• Produits illégaux ou contrefaits
• Armes et munitions
• Drogues et substances illicites
• Contenus frauduleux ou trompeurs
• Animaux protégés
• Médicaments sans prescription
• Produits dangereux ou prohibés par la loi ivoirienne`,
      icon: CheckCircle,
      tags: ['produits', 'autorisés', 'interdits', 'règles'],
      views: 1456,
      helpful: 112,
      notHelpful: 6
    },
    {
      id: 7,
      category: 'listing',
      question: 'Comment modifier ou supprimer mon annonce ?',
      answer: `📝 Pour modifier une annonce :
1. Accédez à "Mes annonces" dans votre profil
2. Sélectionnez l'annonce à modifier
3. Cliquez sur "Modifier"
4. Effectuez vos modifications
5. Enregistrez les changements

🗑️ Pour supprimer une annonce :
1. Accédez à "Mes annonces"
2. Sélectionnez l'annonce
3. Cliquez sur "Supprimer"
4. Confirmez la suppression

⚠️ Une annonce supprimée ne peut pas être récupérée.`,
      icon: Package,
      tags: ['modifier', 'supprimer', 'annonce', 'éditer', 'gérer'],
      views: 789,
      helpful: 67,
      notHelpful: 4
    },
    {
      id: 8,
      category: 'listing',
      question: 'Mon annonce a été supprimée, pourquoi ?',
      answer: `Une annonce peut être supprimée si :
• Elle est frauduleuse ou trompeuse
• Elle viole les Conditions Générales d'Utilisation
• Elle contient des informations fausses
• Elle propose des produits interdits
• Elle ne respecte pas les règles de publication
• Les photos sont inappropriées
• Le contenu est offensant

📧 Si vous pensez qu'il s'agit d'une erreur :
Contactez-nous à contact@monmarket.ci avec :
• Le numéro de votre annonce
• Une explication détaillée
• Des justificatifs si nécessaire

Nous examinerons votre demande sous 48h.`,
      icon: XCircle,
      tags: ['supprimée', 'annonce', 'modération', 'violation'],
      views: 456,
      helpful: 34,
      notHelpful: 12
    },
    {
      id: 9,
      category: 'listing',
      question: 'Combien de temps mon annonce reste-t-elle en ligne ?',
      answer: `⏰ Durée de publication :
• Les annonces restent en ligne pendant 60 jours
• Vous recevrez un rappel 7 jours avant expiration
• Vous pouvez renouveler gratuitement votre annonce

🔄 Pour renouveler :
1. Accédez à "Mes annonces"
2. Cliquez sur "Renouveler" sur l'annonce expirée
3. Confirmez le renouvellement

💡 Astuce : Renouvelez régulièrement vos annonces pour qu'elles apparaissent en tête des résultats.`,
      icon: Clock,
      tags: ['durée', 'annonce', 'expiration', 'renouveler'],
      views: 678,
      helpful: 54,
      notHelpful: 3
    },

    // CONTACT & COMMUNICATION
    {
      id: 10,
      category: 'contact',
      question: 'Comment contacter un vendeur ?',
      answer: `📱 Plusieurs moyens de contact :

1. **Bouton "Contacter"**
   • Cliquez sur "Contacter le vendeur" sur l'annonce
   • Envoyez un message via la plateforme

2. **Téléphone**
   • Appelez directement si le numéro est affiché
   • Privilégiez les appels aux heures normales

3. **Email**
   • Certains vendeurs affichent leur email

💡 Conseils pour un premier contact :
• Présentez-vous clairement
• Posez des questions précises sur le produit
• Soyez poli et respectueux
• Évitez les messages génériques`,
      icon: MessageCircle,
      tags: ['contacter', 'vendeur', 'messagerie', 'communication'],
      views: 1890,
      helpful: 145,
      notHelpful: 7
    },
    {
      id: 11,
      category: 'contact',
      question: 'MonMarket gère-t-il les paiements ?',
      answer: `❌ NON. MonMarket ne gère PAS les paiements.

MonMarket est une plateforme de mise en relation uniquement. Les paiements sont effectués directement entre acheteurs et vendeurs.

⚠️ IMPORTANT :
• MonMarket ne demande JAMAIS d'argent pour finaliser une vente
• Méfiez-vous de toute demande de paiement au nom de MonMarket
• Nous ne sommes pas responsables des transactions entre utilisateurs

🔒 Modes de paiement recommandés :
• En main propre lors de la remise
• Mobile Money en personne
• Virement bancaire après vérification du produit`,
      icon: CreditCard,
      tags: ['paiement', 'argent', 'transaction', 'intermédiaire'],
      views: 2345,
      helpful: 178,
      notHelpful: 15
    },
    {
      id: 12,
      category: 'contact',
      question: 'Je n\'ai pas de réponse du vendeur, que faire ?',
      answer: `Si le vendeur ne répond pas :

⏰ Patience :
• Attendez 48h (il peut être occupé)
• Certains vendeurs ne consultent pas leurs messages immédiatement

📞 Autres moyens :
• Essayez un autre moyen de contact si disponible (téléphone)
• Envoyez un rappel poli après 48h

🔍 Alternatives :
• Cherchez une annonce similaire
• Contactez d'autres vendeurs

ℹ️ À savoir :
• Les vendeurs ne sont pas obligés de répondre
• L'annonce peut être déjà vendue
• Le vendeur peut être inactif`,
      icon: MessageCircle,
      tags: ['réponse', 'vendeur', 'pas de réponse', 'attente'],
      views: 567,
      helpful: 42,
      notHelpful: 8
    },

    // SÉCURITÉ
    {
      id: 13,
      category: 'security',
      question: 'Conseils de sécurité pour acheter en ligne',
      answer: `🛡️ Règles d'or de sécurité :

✅ À FAIRE :
• Rencontrez le vendeur dans un lieu public et fréquenté
• Vérifiez l'état du produit AVANT de payer
• Testez les produits électroniques sur place
• Conservez toutes les preuves de vos échanges
• Faites-vous accompagner si possible

❌ À NE JAMAIS FAIRE :
• Payer sans voir le produit
• Envoyer de l'argent à l'avance
• Donner vos informations bancaires par message
• Accepter des offres trop belles pour être vraies
• Rencontrer dans des endroits isolés

💡 Lieux de rencontre recommandés :
• Centres commerciaux
• Stations-service
• Cafés et restaurants
• Devant une agence bancaire

👉 En cas de doute, ABSTENEZ-VOUS. Votre sécurité prime.`,
      icon: Shield,
      tags: ['sécurité', 'conseils', 'prudence', 'arnaque'],
      views: 3456,
      helpful: 289,
      notHelpful: 12
    },
    {
      id: 14,
      category: 'security',
      question: 'Comment reconnaître une arnaque ?',
      answer: `🚨 Signes d'alerte à surveiller :

🔴 Prix suspect :
• Prix beaucoup trop bas par rapport au marché
• Offres "trop belles pour être vraies"

🔴 Comportement du vendeur :
• Refuse de rencontrer en personne
• Prétend être à l'étranger
• Demande un paiement à l'avance
• Fait pression pour payer rapidement
• Communication étrange ou français approximatif

🔴 Signes techniques :
• Photos volées sur internet
• Profil récent sans historique
• Coordonnées changeantes
• Demande d'informations bancaires sensibles

⚠️ Actions à prendre :
• Ne payez JAMAIS à l'avance
• Signalez immédiatement l'annonce
• Contactez-nous à contact@monmarket.ci
• Bloquez l'utilisateur

✅ Un vendeur fiable :
• Accepte une rencontre en personne
• Répond clairement à vos questions
• Fournit des photos originales
• A un profil avec historique`,
      icon: AlertTriangle,
      tags: ['arnaque', 'fraude', 'scam', 'danger', 'alerte'],
      views: 2789,
      helpful: 234,
      notHelpful: 18
    },
    {
      id: 15,
      category: 'security',
      question: 'Comment signaler une annonce ou un utilisateur ?',
      answer: `📢 Pour signaler un problème :

🖱️ Méthode 1 - Sur l'annonce :
1. Cliquez sur "Signaler" sur l'annonce suspecte
2. Sélectionnez le motif du signalement
3. Ajoutez des détails si nécessaire
4. Validez

📧 Méthode 2 - Par email :
Envoyez un message à contact@monmarket.ci avec :
• Le lien de l'annonce
• Une description détaillée du problème
• Des captures d'écran si possible
• Toute preuve utile (messages, photos)

⚡ Traitement rapide :
• Nous examinons tous les signalements sous 24h
• Les annonces frauduleuses sont supprimées rapidement
• Tous les signalements sont anonymes

🔒 Votre anonymat est garanti.`,
      icon: Flag,
      tags: ['signaler', 'problème', 'fraude', 'arnaque', 'abus'],
      views: 1234,
      helpful: 98,
      notHelpful: 5
    },
    {
      id: 16,
      category: 'security',
      question: 'Que faire en cas d\'arnaque ?',
      answer: `Si vous avez été victime d'une arnaque :

🛑 Actions immédiates :
1. Cessez tout contact avec l'arnaqueur
2. Ne tentez PAS de récupérer votre argent seul
3. Conservez TOUTES les preuves :
   • Messages (SMS, WhatsApp, etc.)
   • Captures d'écran
   • Coordonnées utilisées
   • Preuves de paiement

📢 Signalement :
• Signalez l'annonce et l'utilisateur sur MonMarket
• Contactez-nous : contact@monmarket.ci

🚔 Démarches légales :
• Déposez une plainte auprès de la police/gendarmerie
• Apportez toutes vos preuves
• Demandez un récépissé de dépôt de plainte

💡 Aide complémentaire :
• PLCC (Plateforme de Lutte Contre la Cybercriminalité)
• Contact bancaire si paiement par virement

⚠️ Prévention future :
• Ne payez jamais sans voir le produit
• Privilégiez les rencontres en personne`,
      icon: Shield,
      tags: ['arnaque', 'victime', 'aide', 'plainte', 'police'],
      views: 876,
      helpful: 67,
      notHelpful: 9
    },

    // SUPPORT TECHNIQUE
    {
      id: 17,
      category: 'technical',
      question: 'Le site ne fonctionne pas correctement',
      answer: `🔧 Solutions à essayer dans l'ordre :

1️⃣ Rafraîchir la page
   • Windows/Linux : F5 ou Ctrl+R
   • Mac : Cmd+R

2️⃣ Vider le cache
   • Chrome : Ctrl+Shift+Suppr
   • Firefox : Ctrl+Shift+Suppr
   • Safari : Cmd+Option+E

3️⃣ Changer de navigateur
   • Essayez Chrome, Firefox ou Safari

4️⃣ Vérifier la connexion
   • Testez votre connexion internet
   • Essayez en 4G si vous êtes en WiFi

5️⃣ Redémarrer l'appareil
   • Éteignez et rallumez votre appareil

📧 Si le problème persiste :
Contactez-nous à contact@monmarket.ci en précisant :
• Votre navigateur et sa version
• Le problème exact rencontré
• Des captures d'écran si possible
• Les actions qui ont déclenché le problème`,
      icon: Wrench,
      tags: ['technique', 'bug', 'problème', 'ne fonctionne pas', 'erreur'],
      views: 1567,
      helpful: 123,
      notHelpful: 15
    },
    {
      id: 18,
      category: 'technical',
      question: 'Je ne peux pas télécharger mes photos',
      answer: `📸 Vérifications à faire :

✅ Formats acceptés :
• JPG / JPEG
• PNG
• WEBP

📏 Taille maximale :
• 5 Mo par photo
• Maximum 8 photos par annonce

🔧 Solutions :

1. Réduire la taille :
   • Utilisez un compresseur en ligne (TinyPNG, Compressor.io)
   • Réduisez les dimensions de l'image

2. Convertir le format :
   • Convertissez en JPG si nécessaire
   • Utilisez un convertisseur en ligne

3. Vérifier la connexion :
   • Essayez avec une meilleure connexion
   • Passez de WiFi à 4G ou inversement

4. Essayer un autre navigateur :
   • Chrome, Firefox, Safari

💡 Conseil : Prenez vos photos directement avec votre téléphone en mode "économie" pour avoir des fichiers moins lourds.`,
      icon: Camera,
      tags: ['photos', 'images', 'télécharger', 'upload', 'erreur'],
      views: 1123,
      helpful: 89,
      notHelpful: 11
    },
    {
      id: 19,
      category: 'technical',
      question: 'Pourquoi je ne reçois pas les emails de MonMarket ?',
      answer: `📧 Vérifications à faire :

1️⃣ Vérifier les spams :
   • Regardez dans votre dossier spam/courrier indésirable
   • Marquez nos emails comme "Non spam"

2️⃣ Vérifier la boîte mail :
   • Assurez-vous qu'elle n'est pas pleine
   • Libérez de l'espace si nécessaire

3️⃣ Ajouter à vos contacts :
   • Ajoutez contact@monmarket.ci à vos contacts
   • Ajoutez noreply@monmarket.ci

4️⃣ Vérifier l'adresse email :
   • Connectez-vous à votre profil
   • Vérifiez que l'adresse est correcte
   • Modifiez-la si nécessaire

5️⃣ Fournisseur email :
   • Certains fournisseurs bloquent les emails automatiques
   • Essayez avec Gmail ou Outlook

📧 Toujours rien ?
Contactez-nous avec une adresse email alternative à contact@monmarket.ci`,
      icon: Mail,
      tags: ['email', 'courrier', 'notification', 'spam', 'ne reçois pas'],
      views: 734,
      helpful: 56,
      notHelpful: 8
    },
    {
      id: 20,
      category: 'technical',
      question: 'L\'application mobile est-elle disponible ?',
      answer: `📱 Application mobile MonMarket :

🚧 En développement !
Une application mobile native est actuellement en cours de développement et sera bientôt disponible sur :
• Google Play Store (Android)
• App Store (iOS)

📲 En attendant, vous pouvez :

1. Utiliser le site web mobile
   • Version optimisée pour mobile
   • Accès à toutes les fonctionnalités

2. Ajouter à l'écran d'accueil
   
   Sur Android (Chrome) :
   • Menu ⋮ > Ajouter à l'écran d'accueil
   
   Sur iPhone (Safari) :
   • Partager 🔗 > Sur l'écran d'accueil

   ✨ Vous aurez une icône comme une vraie app !

3. Activer les notifications
   • Autorisez les notifications du navigateur
   • Restez informé en temps réel

📧 Soyez informé du lancement :
Inscrivez-vous à notre newsletter pour être averti dès la sortie de l'application.`,
      icon: Phone,
      tags: ['application', 'mobile', 'app', 'téléphone', 'android', 'ios'],
      views: 2456,
      helpful: 189,
      notHelpful: 23
    }
  ];

  // Filtrage des FAQs
  const filteredFaqs = faqData.filter(faq => {
    const matchesCategory = activeCategory === 'all' || faq.category === activeCategory;
    const matchesSearch = searchTerm === '' || 
      faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchTerm.toLowerCase()) ||
      faq.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  // Stats
  const stats = [
    { label: 'Questions', value: faqData.length, icon: HelpCircle, gradient: 'from-blue-500 to-cyan-500' },
    { label: 'Catégories', value: categories.length - 1, icon: BookOpen, gradient: 'from-purple-500 to-pink-500' },
    { label: 'Support 24/7', value: '✓', icon: Clock, gradient: 'from-green-500 to-emerald-500' }
  ];

  const toggleFaq = (index) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };

  const handleVote = (faqId, isHelpful) => {
    setFaqVotes(prev => ({
      ...prev,
      [faqId]: isHelpful ? 'helpful' : 'notHelpful'
    }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black relative overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 -left-20 w-96 h-96 bg-gradient-to-br from-orange-500/20 to-pink-500/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 -right-20 w-96 h-96 bg-gradient-to-tl from-purple-500/15 to-blue-500/15 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-orange-500/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '500ms' }}></div>
      </div>

      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-2xl bg-white/5 border-b border-white/10 shadow-2xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button 
                onClick={() => navigate(-1)}
                className="group p-2.5 text-white/70 hover:text-purple-400 hover:bg-white/10 rounded-xl transition-all relative overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-orange-500/0 to-orange-500/20 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                <ArrowLeft size={20} className="relative z-10" />
              </button>
              <div 
                className="text-2xl sm:text-3xl font-black bg-gradient-to-r from-orange-400 via-orange-500 to-orange-600 bg-clip-text text-transparent cursor-pointer hover:scale-105 transition-transform"
                onClick={() => navigate('/')}
              >
                MonMarket
              </div>
              <span className="hidden sm:block text-white/20">|</span>
              <span className="hidden sm:flex items-center space-x-2 text-white font-bold">
                <HelpCircle className="text-orange-400" size={20} />
                <span>Centre d'Aide</span>
              </span>
            </div>
            <button 
              onClick={() => navigate('/')}
              className="group p-2.5 text-white/70 hover:text-orange-400 hover:bg-white/10 rounded-xl transition-all relative overflow-hidden"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-orange-500/0 to-orange-500/20 opacity-0 group-hover:opacity-100 transition-opacity"></div>
              <Home size={20} className="relative z-10" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 relative z-10">
        {/* Hero Section */}
        <div className="mb-8 text-center">
          <div className="relative inline-block mb-6">
            <div className="absolute inset-0 bg-orange-500/20 rounded-full blur-2xl animate-pulse"></div>
            <div className="relative w-20 h-20 bg-gradient-to-br from-orange-500 to-pink-500 rounded-full flex items-center justify-center backdrop-blur-xl border-4 border-white/20 shadow-2xl mx-auto">
              <HelpCircle size={40} className="text-white" />
            </div>
          </div>
          <h1 className="text-4xl sm:text-5xl font-black text-white mb-4">
            Centre d'Aide – MonMarket
          </h1>
          <p className="text-lg text-gray-400 max-w-3xl mx-auto mb-2">
            Assistance & accompagnement des utilisateurs en Côte d'Ivoire
          </p>
          <p className="text-gray-500 max-w-2xl mx-auto">
            Trouvez les réponses aux questions les plus fréquentes concernant l'utilisation de la plateforme
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          {stats.map((stat, idx) => (
            <div key={idx} className="group relative">
              <div className={`absolute -inset-0.5 bg-gradient-to-r ${stat.gradient} rounded-2xl blur opacity-0 group-hover:opacity-5 transition duration-500`}></div>
              <div className="relative bg-white/10 backdrop-blur-2xl rounded-2xl p-6 border border-white/20 hover:bg-white/15 transition-all transform hover:scale-105">
                <div className={`w-12 h-12 bg-gradient-to-br ${stat.gradient} rounded-xl flex items-center justify-center mb-4 shadow-lg transform group-hover:rotate-12 transition-transform`}>
                  <stat.icon className="text-white" size={24} />
                </div>
                <div className="text-3xl font-black text-white mb-1">{stat.value}</div>
                <div className="text-sm text-gray-400 font-medium">{stat.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* About Section */}
        <div className="relative group mb-8">
          <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-3xl blur opacity-0 group-hover:opacity-5 transition duration-500"></div>
          <div className="relative bg-white/10 backdrop-blur-2xl rounded-3xl p-8 border border-white/20">
            <div className="flex items-start space-x-4">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-xl">
                <Info size={32} className="text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-black text-white mb-3">🛒 À propos de MonMarket</h2>
                <p className="text-gray-300 leading-relaxed mb-4">
                  MonMarket est une plateforme de mise en relation entre acheteurs et vendeurs en Côte d'Ivoire. 
                  Nous permettons aux utilisateurs de publier des annonces et d'entrer directement en contact, sans intermédiaire imposé.
                </p>
                <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-4 inline-flex items-start space-x-3">
                  <AlertTriangle className="text-orange-400 flex-shrink-0 mt-0.5" size={20} />
                  <p className="text-orange-300 font-medium">
                    MonMarket ne vend aucun produit et n'intervient pas dans les transactions.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Search & Filters */}
        <div className="bg-white/10 backdrop-blur-2xl rounded-2xl p-4 border border-white/20 shadow-xl mb-8">
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Rechercher dans l'aide..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-orange-500/50 focus:bg-white/10 transition-all"
              />
            </div>
          </div>
          
          <div className="flex items-center space-x-2 overflow-x-auto pb-2">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`flex items-center space-x-2 px-4 py-2.5 rounded-xl font-medium text-sm whitespace-nowrap transition-all transform hover:scale-105 ${
                  activeCategory === cat.id
                    ? `bg-gradient-to-r ${cat.gradient} text-white shadow-lg`
                    : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white border border-white/10'
                }`}
              >
                <cat.icon size={16} />
                <span>{cat.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* FAQ List */}
        <div className="space-y-4">
          {filteredFaqs.length === 0 ? (
            <div className="relative group">
              <div className="absolute -inset-0.5 bg-gradient-to-r from-orange-500 to-pink-500 rounded-3xl blur opacity-20 group-hover:opacity-5 transition duration-500"></div>
              <div className="relative bg-white/10 backdrop-blur-2xl rounded-3xl shadow-2xl p-16 text-center border border-white/20">
                <div className="relative inline-block mb-6">
                  <div className="absolute inset-0 bg-orange-500/20 rounded-full blur-2xl animate-pulse"></div>
                  <div className="relative w-24 h-24 bg-white/10 rounded-full flex items-center justify-center backdrop-blur-xl border border-white/20">
                    <Search size={48} className="text-orange-400" />
                  </div>
                </div>
                <h2 className="text-3xl font-black text-white mb-3">Aucun résultat</h2>
                <p className="text-gray-400 mb-8 max-w-md mx-auto">
                  Aucune question ne correspond à votre recherche. Essayez avec d'autres mots-clés ou contactez-nous directement.
                </p>
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setActiveCategory('all');
                  }}
                  className="inline-flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-orange-500 to-pink-500 text-white rounded-xl font-bold hover:shadow-lg hover:scale-105 transition-all"
                >
                  <Search size={20} />
                  <span>Réinitialiser la recherche</span>
                </button>
              </div>
            </div>
          ) : (
            filteredFaqs.map((faq, idx) => {
              const FaqIcon = faq.icon;
              const isExpanded = expandedFaq === idx;
              const userVote = faqVotes[faq.id];
              
              return (
                <div 
                  key={faq.id}
                  className="group relative"
                  style={{ animationDelay: `${idx * 50}ms` }}
                >
                  <div className="absolute -inset-0.5 bg-gradient-to-r from-orange-500 to-pink-500 rounded-2xl blur opacity-0 group-hover:opacity-5 transition duration-500"></div>
                  <div className="relative bg-white/10 backdrop-blur-2xl rounded-2xl border border-white/20 hover:bg-white/15 transition-all overflow-hidden">
                    <button
                      onClick={() => toggleFaq(idx)}
                      className="w-full text-left p-6 flex items-center justify-between space-x-4"
                    >
                      <div className="flex items-center space-x-4 flex-1 min-w-0">
                        <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-pink-500 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
                          <FaqIcon className="text-white" size={24} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-lg font-bold text-white group-hover:text-orange-400 transition-colors mb-2">
                            {faq.question}
                          </h3>
                          <div className="flex items-center space-x-4 text-sm text-gray-500">
                            <span className="flex items-center space-x-1">
                              <Eye size={14} />
                              <span>{faq.views} vues</span>
                            </span>
                            <span className="flex items-center space-x-1">
                              <ThumbsUp size={14} />
                              <span>{faq.helpful + (userVote === 'helpful' ? 1 : 0)}</span>
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className={`w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center transition-transform ${isExpanded ? 'rotate-180' : ''}`}>
                        <ChevronDown className="text-orange-400" size={20} />
                      </div>
                    </button>
                    
                    {isExpanded && (
                      <div className="px-6 pb-6 animate-fadeIn">
                        <div className="pl-16 text-gray-300 leading-relaxed whitespace-pre-line border-l-4 border-orange-500/30 ml-6 mb-4">
                          {faq.answer}
                        </div>
                        
                        {/* Boutons d'aide */}
                        <div className="pl-16 ml-6 flex items-center space-x-4 flex-wrap gap-2">
                          <span className="text-sm text-gray-400">Cette réponse vous a-t-elle aidé ?</span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleVote(faq.id, true);
                            }}
                            disabled={userVote !== undefined}
                            className={`flex items-center space-x-1 px-3 py-1.5 rounded-lg transition-all border text-sm ${
                              userVote === 'helpful'
                                ? 'bg-green-500/30 text-green-300 border-green-500/50'
                                : userVote === 'notHelpful'
                                ? 'bg-white/5 text-gray-500 border-white/10 cursor-not-allowed'
                                : 'bg-green-500/20 text-green-400 hover:bg-green-500/30 border-green-500/30 hover:border-green-500/50'
                            }`}
                          >
                            <ThumbsUp size={14} />
                            <span>Oui</span>
                            {userVote === 'helpful' && <CheckCircle size={14} />}
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleVote(faq.id, false);
                            }}
                            disabled={userVote !== undefined}
                            className={`flex items-center space-x-1 px-3 py-1.5 rounded-lg transition-all border text-sm ${
                              userVote === 'notHelpful'
                                ? 'bg-red-500/30 text-red-300 border-red-500/50'
                                : userVote === 'helpful'
                                ? 'bg-white/5 text-gray-500 border-white/10 cursor-not-allowed'
                                : 'bg-red-500/20 text-red-400 hover:bg-red-500/30 border-red-500/30 hover:border-red-500/50'
                            }`}
                          >
                            <ThumbsDown size={14} />
                            <span>Non</span>
                            {userVote === 'notHelpful' && <CheckCircle size={14} />}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Contact Section */}
        <div className="mt-12 relative group">
          <div className="absolute -inset-0.5 bg-gradient-to-r from-green-500 to-emerald-500 rounded-3xl blur opacity-0 group-hover:opacity-5 transition duration-500"></div>
          <div className="relative bg-white/10 backdrop-blur-2xl rounded-3xl p-8 border border-white/20">
            <h2 className="text-2xl font-black text-white mb-6 flex items-center space-x-3">
              <MessageCircle className="text-green-400" size={28} />
              <span>📩 Contact & Assistance</span>
            </h2>
            <p className="text-gray-400 mb-6">Besoin d'aide supplémentaire ?</p>
            
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-white/5 rounded-xl p-4 border border-white/10 hover:border-green-500/30 transition-all">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl flex items-center justify-center">
                    <Mail className="text-white" size={20} />
                  </div>
                  <div>
                    <div className="text-xs text-gray-400 mb-1">Email</div>
                    <div className="text-white font-bold">contact@monmarket.ci</div>
                  </div>
                </div>
              </div>
              
              <div className="bg-white/5 rounded-xl p-4 border border-white/10 hover:border-blue-500/30 transition-all">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center">
                    <MapPin className="text-white" size={20} />
                  </div>
                  <div>
                    <div className="text-xs text-gray-400 mb-1">Localisation</div>
                    <div className="text-white font-bold">Côte d'Ivoire</div>
                  </div>
                </div>
              </div>
              
              <div className="bg-white/5 rounded-xl p-4 border border-white/10 hover:border-purple-500/30 transition-all">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
                    <Clock className="text-white" size={20} />
                  </div>
                  <div>
                    <div className="text-xs text-gray-400 mb-1">Disponibilité</div>
                    <div className="text-white font-bold">Selon horaires</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Legal Links */}
        <div className="mt-8 text-center">
          <div className="inline-flex items-center space-x-2 text-sm text-gray-500">
            <FileText size={16} />
            <span>Documents légaux :</span>
            <a href="/cgu" className="text-orange-400 hover:text-orange-300 transition-colors font-medium">CGU</a>
            <span>•</span>
            <a href="/cgu#confidentialite" className="text-orange-400 hover:text-orange-300 transition-colors font-medium">Confidentialité</a>
            <span>•</span>
            <a href="/cgu#mentions" className="text-orange-400 hover:text-orange-300 transition-colors font-medium">Mentions Légales</a>
          </div>
        </div>
      </main>
    </div>
  );
}